/**
 * Tiny event API that lets workspace chrome (e.g. an Activity FAB or
 * keyboard shortcut) ask a specific edge panel to open and optionally
 * select a sub-tab. Decoupled from React state so triggers don't need to
 * own a ref to the panel component.
 */

import { useEffect, useState } from 'react';

export type PanelOpenDetail = {
  /** Matches the `id` prop on the target `PanelShell`. */
  id: string;
  /** Optional sub-tab the panel should select once open. */
  tab?: string;
  /**
   * If true, the panel flips its open state instead of forcing open.
   * Used by toggle-style chrome triggers (the Notifications bell and the
   * Activity FAB) so a second click closes the panel they opened.
   */
  toggle?: boolean;
  /** If true, force the panel CLOSED (used by auto-hide to also hide the panel). */
  close?: boolean;
  /**
   * If true, the panel should open in its fullscreen / expanded view
   * variant. Currently honoured by the bottom panel, which exposes
   * Marketplace + Widgets either as a content-width popover or as a
   * full-screen dialog. Callers (e.g. the workspace + FAB or canvas
   * context menu) use this to jump straight to the fullscreen mode.
   */
  expanded?: boolean;
  /**
   * If true, the PanelShell itself should NOT open the underlying
   * popover — only the expanded fullscreen overlay should appear.
   * Used by the search bar's "Open discovery" CTA so the bottom Zap
   * popover doesn't quietly open behind the fullscreen Discover view
   * (and stay visible after the user closes the fullscreen).
   * Implies `expanded: true`.
   */
  expandedOnly?: boolean;
  /**
   * Optional initial content-type filter for a panel's Discover-style
   * sub-tab (kept for API compatibility; the bottom marketplace panel
   * that minted this option is retired — Discover overlay owns browse).
   */
  contentType?: string;
};

const EVENT_NAME = 'workspace-panel-open';

export function openWorkspacePanel(
  id: string,
  opts?: {
    tab?: string;
    expanded?: boolean;
    expandedOnly?: boolean;
    contentType?: string;
  },
): void {
  if (typeof window === 'undefined') return;
  // Eagerly publish the next open-state so FAB chrome subscribers
  // ("am I the open one?") update SYNCHRONOUSLY on click, before the
  // target PanelShell mounts/renders its heavy children. Without this
  // eager echo, the FAB's pressed-state animation appeared to lag
  // because it waited for the panel's mount→useEffect cycle to round-
  // trip through `notifyWorkspacePanelState`. The panel will re-emit
  // the same state on its next render — idempotent and harmless.
  //
  // When `expandedOnly` is set the popover stays hidden — we don't
  // want the chrome to claim "open" for the FAB either.
  if (!opts?.expandedOnly) notifyWorkspacePanelState(id, true);
  // Publish the active view to the shared mirror so the rail's highlight and the
  // panel body read ONE value (single source of truth — they can't drift).
  if (opts?.tab) notifyWorkspacePanelTab(id, opts.tab);
  window.dispatchEvent(
    new CustomEvent<PanelOpenDetail>(EVENT_NAME, {
      detail: {
        id,
        ...(opts?.tab ? { tab: opts.tab } : {}),
        ...(opts?.expanded || opts?.expandedOnly ? { expanded: true } : {}),
        ...(opts?.expandedOnly ? { expandedOnly: true } : {}),
        ...(opts?.contentType ? { contentType: opts.contentType } : {}),
      },
    }),
  );
}

/**
 * Toggle the panel referenced by `id`. If it's already open it closes,
 * otherwise it opens (and optionally selects a tab). Designed for FAB
 * triggers where the same button should act as both "show" and "hide".
 */
export function toggleWorkspacePanel(
  id: string,
  opts?: { tab?: string; expanded?: boolean; contentType?: string },
): void {
  if (typeof window === 'undefined') return;
  // Same eager-publish trick as `openWorkspacePanel` — read the last
  // known state synchronously and broadcast its negation immediately
  // so the FAB's pressed surface (`ws-fab-pressed`) lights up on the
  // very same frame as the click instead of waiting for the panel to
  // re-render. The PanelShell still flips its real state via the
  // toggle event below; if our cached state somehow disagrees the
  // panel's eventual `notifyWorkspacePanelState` will reconcile.
  notifyWorkspacePanelState(id, !getWorkspacePanelState(id));
  if (opts?.tab) notifyWorkspacePanelTab(id, opts.tab);
  window.dispatchEvent(
    new CustomEvent<PanelOpenDetail>(EVENT_NAME, {
      detail: {
        id,
        toggle: true,
        ...(opts?.tab ? { tab: opts.tab } : {}),
        ...(opts?.expanded ? { expanded: true } : {}),
        ...(opts?.contentType ? { contentType: opts.contentType } : {}),
      },
    }),
  );
}

/** Force a panel closed (e.g. auto-hide should also hide an open panel). */
export function closeWorkspacePanel(id: string): void {
  if (typeof window === 'undefined') return;
  notifyWorkspacePanelState(id, false);
  window.dispatchEvent(
    new CustomEvent<PanelOpenDetail>(EVENT_NAME, { detail: { id, close: true } }),
  );
}

export function subscribeWorkspacePanelOpen(
  handler: (detail: PanelOpenDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (e: Event) =>
    handler((e as CustomEvent<PanelOpenDetail>).detail);
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}

/**
 * Open-state mirror for panels.
 *
 * Each `PanelShell` calls `notifyWorkspacePanelState(id, open)` whenever
 * its internal `open` state changes. Chrome that needs to reflect the
 * panel's state (e.g. a FAB rendering an X icon when its panel is open)
 * subscribes via `subscribeWorkspacePanelState`. We back the live state
 * with a module-scoped `Map` so newly mounted listeners can also read
 * the current value synchronously through `getWorkspacePanelState`.
 */
export type PanelStateDetail = {
  id: string;
  open: boolean;
};

const STATE_EVENT_NAME = 'workspace-panel-state';
const panelStateById = new Map<string, boolean>();

export function notifyWorkspacePanelState(id: string, open: boolean): void {
  panelStateById.set(id, open);
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<PanelStateDetail>(STATE_EVENT_NAME, {
      detail: { id, open },
    }),
  );
}

export function getWorkspacePanelState(id: string): boolean {
  return panelStateById.get(id) ?? false;
}

export function subscribeWorkspacePanelState(
  handler: (detail: PanelStateDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (e: Event) =>
    handler((e as CustomEvent<PanelStateDetail>).detail);
  window.addEventListener(STATE_EVENT_NAME, listener);
  return () => window.removeEventListener(STATE_EVENT_NAME, listener);
}

/**
 * React hook returning the live open-state of the panel with `id`.
 * Backed by the same event channel as `subscribeWorkspacePanelState`,
 * with a synchronous initial read so the first render already reflects
 * the panel's current state (avoids a single-frame flicker on the FAB).
 */
export function useWorkspacePanelOpen(id: string): boolean {
  const [open, setOpen] = useState<boolean>(() => getWorkspacePanelState(id));
  useEffect(() => {
    setOpen(getWorkspacePanelState(id));
    return subscribeWorkspacePanelState((detail) => {
      if (detail.id === id) setOpen(detail.open);
    });
  }, [id]);
  return open;
}

/**
 * Active-view mirror — the single source of truth for WHICH view a docked
 * panel is showing.
 *
 * The rail and the panel body must agree on the active view, or the lit rail
 * icon drifts from what's actually shown. We mirror the active tab per panel id
 * exactly like the open-state above: `openWorkspacePanel(id, { tab })` publishes
 * it eagerly, the panel body reads it via `useWorkspacePanelTab(id)` (no local
 * tab state), and the rail derives its highlight from the same value. One
 * writer per click, one value both sides read.
 */
/**
 * Content panel create trigger. The Content view's create is a small
 * menu (New folder / Upload), so unlike the other panels it can't be a
 * one-shot `createActionFor` callback. Instead the panel header's "+"
 * (rendered in `left-panel`, in the SAME slot as every other view's
 * create button) fires this event, and `MyContentManager` opens its
 * own anchored menu in response. Keeps the "+" in the consistent header
 * position while leaving the create logic where it lives.
 */
const CONTENT_CREATE_EVENT = 'workspace-content-create';

export function requestContentCreate(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONTENT_CREATE_EVENT));
}

export function subscribeContentCreate(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = () => handler();
  window.addEventListener(CONTENT_CREATE_EVENT, listener);
  return () => window.removeEventListener(CONTENT_CREATE_EVENT, listener);
}

/**
 * Open a post's thread in the Feed panel's detail view.
 *
 * The detail stack is local state inside `left-panel`, so surfaces outside
 * it (the shared-post preview dialog, which is where a `/p/<id>` link lands)
 * had no way to reach the thread — the dialog offered Save and Follow and
 * nothing that let you actually read or reply. Same shape as the content
 * create trigger above: fire the event, the panel that owns the stack acts.
 */
const POST_OPEN_EVENT = 'workspace-post-open';
let pendingPostOpen: string | null = null;

export function requestPostOpen(postId: string): void {
  // Park the request as well as dispatching it. The caller usually opens the
  // panel in the same tick, so the Feed view that owns the detail stack has
  // not mounted its subscription yet and a plain event would be missed.
  pendingPostOpen = postId;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(POST_OPEN_EVENT, { detail: { postId } }));
}

export function subscribePostOpen(
  handler: (postId: string) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  // Drain anything requested before this subscriber existed.
  if (pendingPostOpen) {
    const id = pendingPostOpen;
    pendingPostOpen = null;
    handler(id);
  }
  const listener = (e: Event) => {
    const id = (e as CustomEvent<{ postId?: string }>).detail?.postId;
    if (!id) return;
    pendingPostOpen = null;
    handler(id);
  };
  window.addEventListener(POST_OPEN_EVENT, listener);
  return () => window.removeEventListener(POST_OPEN_EVENT, listener);
}

/**
 * Content panel "browse this folder" hand-off. The Workspaces view's
 * "Browse files" row opens the Content panel scoped to a workspace folder;
 * the tab switch and the event race (the Content view may not be mounted
 * yet), so the request is parked like `requestPostOpen` and consumed on
 * mount via `consumePendingContentBrowse`.
 */
export type ContentBrowseTarget = {
  id: string;
  name: string;
  /** Folder the viewer does not own — browse through the shared branch. */
  shared?: boolean;
};

const CONTENT_BROWSE_EVENT = 'workspace-content-browse';
let pendingContentBrowse: ContentBrowseTarget | null = null;

export function requestContentBrowse(target: ContentBrowseTarget): void {
  pendingContentBrowse = target;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONTENT_BROWSE_EVENT, { detail: target }));
}

export function consumePendingContentBrowse(): ContentBrowseTarget | null {
  const t = pendingContentBrowse;
  pendingContentBrowse = null;
  return t;
}

export function subscribeContentBrowse(
  handler: (target: ContentBrowseTarget) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<ContentBrowseTarget>).detail;
    if (!detail?.id) return;
    pendingContentBrowse = null;
    handler(detail);
  };
  window.addEventListener(CONTENT_BROWSE_EVENT, listener);
  return () => window.removeEventListener(CONTENT_BROWSE_EVENT, listener);
}

export type PanelTabDetail = { id: string; tab: string };

const TAB_EVENT_NAME = 'workspace-panel-tab';
const panelTabById = new Map<string, string>();

export function notifyWorkspacePanelTab(id: string, tab: string): void {
  panelTabById.set(id, tab);
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<PanelTabDetail>(TAB_EVENT_NAME, { detail: { id, tab } }),
  );
}

export function getWorkspacePanelTab(id: string): string | undefined {
  return panelTabById.get(id);
}

/**
 * React hook returning the live active view of the panel with `id`
 * (`undefined` until first set). Synchronous initial read avoids a flicker.
 */
export function useWorkspacePanelTab(id: string): string | undefined {
  const [tab, setTab] = useState<string | undefined>(() =>
    getWorkspacePanelTab(id),
  );
  useEffect(() => {
    setTab(getWorkspacePanelTab(id));
    const listener = (e: Event) => {
      const detail = (e as CustomEvent<PanelTabDetail>).detail;
      if (detail.id === id) setTab(detail.tab);
    };
    window.addEventListener(TAB_EVENT_NAME, listener);
    return () => window.removeEventListener(TAB_EVENT_NAME, listener);
  }, [id]);
  return tab;
}
